import { ApplicationRef, Component, inject, Inject, OnInit, PLATFORM_ID } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FlowbiteService } from '../shared/services/flowbite.service';
import { LucideAngularModule } from 'lucide-angular';
import { ActivatedRoute } from '@angular/router';
import { UserDataService } from '../shared/services/userData.service';
import { FullCalendarModule } from '@fullcalendar/angular';
import { LargeSidebarComponent } from "./sidebar/large-sidebar/large-sidebar.component";
import { SmallSidebarComponent } from "./sidebar/small-sidebar/small-sidebar.component";
import { NsideIdentityService } from '../shared/services/auth/nSideIdentityService';
import { filter, map } from 'rxjs';
import { DEFAULT_EOP_PLAN, EopPlanSlug, getBuildingIdFromUrl, getPlanSlugFromUrl } from './eop-config/eop-page-config';
declare var initFlowbite: any;


@Component({
  selector: 'app-root',
  standalone: true,
  imports: [
    RouterOutlet,
    CommonModule,
    LucideAngularModule,
    FullCalendarModule,
    LargeSidebarComponent,
    SmallSidebarComponent
],
  templateUrl: './app.component.html',
  styleUrl: './app.component.css'
})
export class AppComponent implements OnInit {
  title = 'REMs-EOP';
  userId: string | null = null;
  buildingId: string | null = null;
  currentPlan: EopPlanSlug = DEFAULT_EOP_PLAN;
  sidebarStatus: string = '';
  showSidebar: boolean = true;
  private readonly nSideIdentityService = inject(NsideIdentityService);
  private readonly activatedRoute = inject(ActivatedRoute);

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private router: Router,
    private userDataService: UserDataService
  ) {
    // Optional: Monitor app stability
    // appRef.isStable.subscribe(stable => {
    //   console.log('App is stable?', stable);
    // });
  }

  ngOnInit(): void {
    initFlowbite(); // Initialize Flowbite components

    // Sidebar visibility control
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => {
          let currentRoute = this.activatedRoute.root;
          while (currentRoute.firstChild) {
            currentRoute = currentRoute.firstChild;
          }
          return currentRoute;
        }),
        map((route) => route.snapshot.data)
      )
      .subscribe((data) => {
        this.showSidebar = data['showSidebar'] !== false; // Set sidebar visibility based on route data
      });

    if (isPlatformBrowser(this.platformId)) {
      // Retrieve sidebar status from session storage
      const savedSidebarStatus = sessionStorage.getItem('sidebarStatus');
      this.sidebarStatus = savedSidebarStatus ? savedSidebarStatus : 'large';

      // Delay the Flowbite initialization to ensure the DOM is ready
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe(() => {
          setTimeout(() => {
            initFlowbite();
          }, 100);
        });

      const syncRouteState = (url: string) => {
        const buildingId = getBuildingIdFromUrl(url) ?? localStorage.getItem('current_building_id');
        const planSlug = getPlanSlugFromUrl(url) ?? DEFAULT_EOP_PLAN;

        if (buildingId) {
          this.buildingId = buildingId;
          this.userDataService.setBuildingId(buildingId);
          localStorage.setItem('current_building_id', buildingId);
        }

        this.currentPlan = planSlug;
        this.userDataService.setPlanSlug(planSlug);
      };

      syncRouteState(this.router.url);
      this.router.events
        .pipe(filter((event) => event instanceof NavigationEnd))
        .subscribe((event) => {
          syncRouteState((event as NavigationEnd).urlAfterRedirects);
        });
    }
  }

  toggleSidebarStatus(status: string): void {
    this.sidebarStatus = status;
    if (isPlatformBrowser(this.platformId)) {
      sessionStorage.setItem('sidebarStatus', status);
    }
  }
}
